import { Card } from '@/types/game';
import { LEVEL_CONFIG } from '@/data/levels';
import { coversPosition, generateBoardLayout } from '@/data/board';
import { Difficulty } from '@/data/settings';

export const generateId = (len: number = 6): string => {
  const pool = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let res = '';
  while (len > 0) {
    res += pool[Math.floor(pool.length * Math.random())];
    len--;
  }
  return res;
};

export const waitTimeout = (timeout: number): Promise<void> => {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, timeout);
  });
};

export const timestampToUsedTimeString = (time: number): string => {
  try {
    const hours = Math.floor(time / (1000 * 60 * 60));
    const minutes = Math.floor(
      (time - 1000 * 60 * 60 * hours) / (1000 * 60)
    );
    const seconds = (
      (time - 1000 * 60 * 60 * hours - 1000 * 60 * minutes) /
      1000
    ).toFixed(1);
    if (hours) {
      return `${hours}小时${minutes}分${seconds}秒`;
    } else if (minutes) {
      return `${minutes}分${seconds}秒`;
    } else {
      return `${seconds}秒`;
    }
  } catch (e) {
    return '时间转换出错';
  }
};


/** Generate a broad, aligned board and preserve a legal no-prop route. */
export const generateCards = (level: number, cardTypes: string[], rng: () => number = Math.random, difficulty: Difficulty = 'normal'): Card[] => {
  const normalizedLevel = Math.max(1, Math.min(LEVEL_CONFIG.length, Math.floor(level)));
  const config = LEVEL_CONFIG[normalizedLevel - 1];
  const types = cardTypes.slice(0, config.types);
  if (types.length === 0) return [];
  const positions = generateBoardLayout(normalizedLevel, types.length * config.cardsPerType, config.layers);
  const boardId = generateId(8);
  const cards: Card[] = positions.map((position, index) => ({
    ...position, id: boardId + '-' + index, type: '', emoji: '', isCover: false, status: 0
  }));
  // Descending zIndex is always a legal reveal order. Assign interleaved triples
  // along that order, keeping at most six stable slot occupants. This proves
  // existence of a route; players may still lose after choosing another route.
  const remaining = new Map(types.map(type => [type, config.cardsPerType]));
  const inSlot = new Map<string, number>();
  let occupied = 0;
  const maxOpenTypes = normalizedLevel === 1 ? 2 : { easy: 2, normal: 4, hard: 5 }[difficulty];
  for (const card of [...cards].sort((a, b) => b.zIndex - a.zIndex)) {
    const candidates = types.filter(type => {
      const count = inSlot.get(type) || 0;
      return (remaining.get(type) || 0) > 0
        && (count === 2 || (occupied < 6 && (count > 0 || inSlot.size < maxOpenTypes)));
    });
    if (candidates.length === 0) throw new Error('No valid triple scheduling candidate');
    const type = candidates[Math.floor(rng() * candidates.length)];
    card.type = type;
    card.emoji = type;
    remaining.set(type, remaining.get(type)! - 1);
    const next = (inSlot.get(type) || 0) + 1;
    if (next === 3) { inSlot.delete(type); occupied -= 2; }
    else { inSlot.set(type, next); occupied++; }
  }
  return checkCover(cards);
};

export const fastShuffle = <T>(arr: T[]): T[] => {
  const res = arr.slice();
  for (let i = 0; i < res.length; i++) {
    const idx = Math.floor(Math.random() * res.length);
    [res[i], res[idx]] = [res[idx], res[i]];
  }
  return res;
};

let coverLayout: { positions: Pick<Card, 'x' | 'y' | 'zIndex' | 'zone' | 'size' | 'sourceId' | 'layer'>[]; blockers: number[][] } | null = null;
export const checkCover = (cards: Card[]): Card[] => {
  if (!coverLayout || cards.length !== coverLayout.positions.length || cards.some((card, i) => {
    const p = coverLayout!.positions[i];
    return card.x !== p.x || card.y !== p.y || card.zIndex !== p.zIndex || card.zone !== p.zone || card.size !== p.size || card.layer !== p.layer || card.sourceId !== p.sourceId;
  })) {
    coverLayout = {
      positions: cards.map(({ x, y, zIndex, zone, size, sourceId, layer }) => ({ x, y, zIndex, zone, size, sourceId, layer })),
      blockers: cards.map(card => cards.flatMap((other, i) => coversPosition(card, other) ? [i] : []))
    };
  }
  const { blockers } = coverLayout;
  return cards.map((cur, i) => {
    const covered = cur.status === 0 && blockers[i].some(index => cards[index].status === 0);
    return cur.isCover === covered ? cur : { ...cur, isCover: covered };
  });
};
export const sortQueueCards = (queue: Card[]): Card[] => {
  const cache: Record<string, Card[]> = {};
  
  for (const card of queue) {
    const key = card.type;
    if (cache[key]) {
      cache[key].push(card);
    } else {
      cache[key] = [card];
    }
  }
  
  const temp: Card[] = [];
  for (const cards of Object.values(cache)) {
    temp.push(...cards);
  }
  
  return temp;
};

export const sortQueue = (queue: Card[]): Record<string, number> => {
  const sortedCards = sortQueueCards(queue);
  const sortedQueue: Record<string, number> = {};
  let x = 50;
  for (const card of sortedCards) {
    sortedQueue[card.id] = x;
    x += 90;
  }
  
  return sortedQueue;
};

export const isGameOver = (queue: Card[]): boolean => {
  return queue.length === 7;
};

export const isWin = (cards: Card[]): boolean => {
  return !cards.find((c) => c.status !== 2);
};

export const debugCardCounts = (cards: Card[], prefix: string = ''): Record<string, number> => {
  const counts: Record<string, number> = {};
  cards.forEach(card => {
    const key = card.type;
    if (counts[key]) {
      counts[key]++;
    } else {
      counts[key] = 1;
    }
  });
  
  if (prefix) {
    console.log(`[${prefix}] 卡牌分布:`, counts);
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    console.log(`[${prefix}] 总牌数:`, total);
  }
  
  return counts;
};

export const debugGameState = (cards: Card[], queue: Card[], prefix: string = ''): void => {
  if (prefix) {
    console.log(`=== [${prefix}] 游戏状态调试 ===`);
  }
  
  const activeCards = cards.filter(c => c.status === 0);
  const queuedCards = cards.filter(c => c.status === 1);
  const eliminatedCards = cards.filter(c => c.status === 2);
  
  console.log('场上活跃牌 (status=0):', debugCardCounts(activeCards));
  console.log('卡槽中的牌 (status=1):', debugCardCounts(queuedCards));
  console.log('已消除的牌 (status=2):', debugCardCounts(eliminatedCards));
  
  const queueTypes: Record<string, number> = {};
  queue.forEach(card => {
    const key = card.type;
    if (queueTypes[key]) {
      queueTypes[key]++;
    } else {
      queueTypes[key] = 1;
    }
  });
  console.log('当前卡槽内容:', queueTypes);
  console.log('====================================');
};

export const checkSolvabilityHeuristic = (cards: Card[]): boolean => {
  const activeCards = cards.filter(c => c.status === 0);
  const cardCounts: Record<string, number> = {};
  
  activeCards.forEach(card => {
    const key = card.type;
    if (cardCounts[key]) {
      cardCounts[key]++;
    } else {
      cardCounts[key] = 1;
    }
  });
  
  for (const type in cardCounts) {
    if (cardCounts[type] % 3 !== 0) {
      console.warn(`[可解性预检查] 警告: ${type} 类型的卡牌数量 ${cardCounts[type]} 不是 3 的倍数!`);
      return false;
    }
  }
  
  console.log('[可解性预检查] 通过: 所有类型卡牌数量都是 3 的倍数');
  return true;
};
